import React from "react";
import { Box, Button, Flex, HStack, Text, Tooltip } from "@chakra-ui/react";
import { CalendarBlank, CaretLeft, TreeStructure, WarningCircle } from "@phosphor-icons/react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../config/paths";
import { useSession } from "../state/useSession";
import { useAiIngestTemplateQuery } from "../../../hooks/query/questionnaire";
import { formatPeriod, formatTarget, pinnedDimensions, scopePillFor, taskHref } from "./taskLabels";
import { useFoundationV2Years } from "../../../hooks/query/issb";

/**
 * Which data task this session writes into, and the way back to it.
 *
 * The page is only ever reached from a task's data grid, but once here nothing said WHICH task — the
 * destination and the reporting period were known to the agent and invisible to the user. That is the
 * wrong way round for a screen whose entire output is written into that task.
 *
 * Two deliberate choices about where each fact comes from:
 *
 *  - Target from the SESSION, not the URL. A bookmarked link can carry a stale `?target=`, and the server
 *    drops one it does not recognise, so the URL can disagree with what the session actually commits
 *    against. Showing the URL's version would be showing the wrong destination confidently.
 *  - Period from the JAVA descriptor, not the data-sorting session. Java is authoritative and answers
 *    immediately; the session's copy only exists after the agent has called get_task_template, so reading
 *    it there would leave the bar blank until the first turn.
 *
 * Only icons already proven to import elsewhere in this app are used here (CaretLeft, from the
 * foundation bar below and WorkingPanel; FaArrowLeft, from the
 * OrganizationQuestionnaire "Tasks" back link this button's styling matches; CalendarBlank/Flame/
 * Lightning/Truck/TreeStructure/WarningCircle, from the Organization Planning task cards' scope/period/
 * dimension rows and Chakra's own icon set).
 * `node_modules` is not installable in every environment this is edited from, so an unverified icon name
 * is a render-time crash that no amount of reading catches — text labels carry the meaning anyway.
 */
export function TaskBar() {
  const { sessionId, questionnaireId, aiIngestTarget, sessionKind, foundationYearId } = useSession();
  const navigate = useNavigate();
  const isFoundation = sessionKind === "foundation_v2";

  // A foundation session has no data task, so there is no template to ask Java for. The hook already
  // disables itself without a questionnaireId (which is null here), but saying so explicitly is what makes
  // that a decision rather than a coincidence — note `enabled` belongs in the SECOND argument; passing it
  // in the params object would be silently dropped.
  const { data: descriptor } = useAiIngestTemplateQuery(
    { questionnaireId, target: aiIngestTarget },
    { enabled: !isFoundation && Boolean(questionnaireId && aiIngestTarget) },
  );

  const { data: years } = useFoundationV2Years({ enabled: isFoundation });
  /*
    The workstream as well as the year. A year holds three — GHG Emissions, Risks and Opportunities, Targets —
    so a bar reading only "2026" does not say which set of figures this session is about, and all three look
    identical in the session picker.
  */
  const dataYear = isFoundation
    ? (years || []).find((y) => String(y.id) === String(foundationYearId))
    : null;
  const destination = dataYear ? `${dataYear.questionGroup} ${dataYear.year}` : null;

  // Render nothing until the session resolves. A half-empty bar that then jumps is worse than a moment
  // with no bar.
  if (!sessionId) return null;
  if (isFoundation) {
    return (
      <FoundationBar
        destination={destination}
        onBack={() => navigate(routes.foundationDataV2)}
      />
    );
  }
  if (!questionnaireId) return null;

  const target = formatTarget(aiIngestTarget);
  const period = formatPeriod(descriptor);
  const scopePill = scopePillFor(aiIngestTarget);
  const ScopeIcon = scopePill?.icon;
  // See taskLabels.pinnedDimensions — this reads the dimension pin out of Java's field description
  // prose, because AiIngestTemplateDto has no dedicated field for it. The WarningCircle note below is
  // the visible flag for that, not just a code comment.
  const dimensions = pinnedDimensions(descriptor);

  return (
    <Flex
      align="center"
      flexShrink={0}
      h="52px"
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Box
        px={5}
        h="100%"
        display="flex"
        alignItems="center"
        borderRight="1px solid"
        borderColor="gray.200"
        flexShrink={0}
      >
        <Button
          variant="link"
          color="limeShade.600"
          leftIcon={<FaArrowLeft />}
          fontSize="14px"
          fontWeight="600"
          _hover={{ textDecoration: "none" }}
          onClick={() => navigate(taskHref(routes.organisationQuestionnaire, questionnaireId))}
        >
          <Text as="span" color="whiteShade.600" fontSize="14px" fontWeight="600">
            Back to task
          </Text>
        </Button>
      </Box>

      <HStack spacing={0} h="100%" minW={0} align="center">
        <Box
          px={5}
          h="100%"
          display="flex"
          alignItems="center"
          borderRight={period ? "1px solid" : undefined}
          borderColor="gray.200"
        >
          <HStack spacing={3} minW={0} align="center">
            {target && (
              <HStack spacing={2} minW={0} align="center">
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap="4px"
                  bg={scopePill?.bg || "gray.100"}
                  color={scopePill?.color || "darkTeal"}
                  fontSize="13px"
                  fontWeight="600"
                  px="10px"
                  h="24px"
                  borderRadius="full"
                  whiteSpace="nowrap"
                  flexShrink={0}
                >
                  {ScopeIcon && <ScopeIcon size={13} weight="fill" />}
                  <Text as="span" noOfLines={1}>
                    {target}
                  </Text>
                </Box>
              </HStack>
            )}
          </HStack>
        </Box>

        {period && (
          <Box
            px={5}
            h="100%"
            display="flex"
            alignItems="center"
            borderRight={dimensions.length ? "1px solid" : undefined}
            borderColor="gray.200"
          >
            <HStack spacing={2} flexShrink={0} align="center">
              <CalendarBlank size={13} color="#A0AEC0" />
              <Text fontSize="11px" color="gray.500">
                Period
              </Text>
              {/* The period is fixed by the task and never becomes a row column — said here for the same
                  reason the agent's prompt says it. */}
              <Tooltip
                label="Fixed by the task. Every row committed from this session belongs to this period."
                openDelay={400}
              >
                <Text fontSize="14px" fontWeight="600" color="gray.700">
                  {period}
                </Text>
              </Tooltip>
            </HStack>
          </Box>
        )}

        {dimensions.length > 0 && (
          <Box px={5} h="100%" display="flex" alignItems="center">
            <HStack spacing={3} flexShrink={0} align="center">
              {dimensions.map((dim) => (
                <Tooltip key={dim.level} label={dim.level} openDelay={400}>
                  <HStack spacing={1.5} align="center">
                    <TreeStructure size={13} color="#A0AEC0" />
                    <Text fontSize="13px" color="gray.500">
                      {dim.value}
                    </Text>
                  </HStack>
                </Tooltip>
              ))}
              {/* Visible on the page, not just in a code comment: this reads Java's free-text field
                  description to find the pin (see taskLabels.pinnedDimensions) because
                  AiIngestTemplateDto has no field of its own for it. Backend TODO: add a dedicated
                  pinned-dimension field to that DTO so this stops depending on prose it doesn't own. */}
              <Tooltip
                label={
                  "Parsed from the ingest template's field description text, not a dedicated field — " +
                  "backend TODO: AiIngestTemplateDto should expose the pinned dimension explicitly."
                }
                openDelay={200}
              >
                <Box display="flex" alignItems="center">
                  <WarningCircle size={14} color="#DD6B20" weight="fill" />
                </Box>
              </Tooltip>
            </HStack>
          </Box>
        )}
      </HStack>
    </Flex>
  );
}

/**
 * The foundation twin: which year is being curated, and the way back to it.
 *
 * A separate component rather than more branches inside the bar above. The two share only a shape — one
 * names a task, a destination and a fixed reporting period, the other names a year — and interleaving
 * them would leave every line of both guarded by the same condition.
 */
function FoundationBar({ destination, onBack }) {
  return (
    <Flex
      align="center"
      justify="space-between"
      flexShrink={0}
      px={6}
      py={2}
      gap={4}
      bg="white"
      borderBottom="1px solid"
      borderColor="gray.100"
    >
      <HStack spacing={6} minW={0}>
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.06em"
          color="gray.500"
          textTransform="uppercase"
          flexShrink={0}
        >
          Foundation data
        </Text>
        {destination && (
          <HStack spacing={2} minW={0}>
            <Text fontSize="11px" color="gray.500" flexShrink={0}>
              Workstream
            </Text>
            <Text fontSize="13px" fontWeight="600" color="darkTeal" noOfLines={1}>
              {destination}
            </Text>
          </HStack>
        )}
        <HStack spacing={2} minW={0}>
          <Tooltip
            label="Saving here writes to a separate curated set. The extracted rows are never changed."
            openDelay={400}
          >
            <Text fontSize="13px" fontWeight="600" color="gray.700">
              Curating extracted metrics
            </Text>
          </Tooltip>
        </HStack>
      </HStack>

      <Box flexShrink={0}>
        <Button
          size="xs"
          variant="secondary"
          leftIcon={<CaretLeft size={12} weight="bold" />}
          onClick={onBack}
        >
          Back to Foundation Data
        </Button>
      </Box>
    </Flex>
  );
}

export default TaskBar;
